import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSiteMenus } from '@/hooks/useSiteMenus';
import { useMegaMenu, MegaMenuItem } from '@/hooks/useMegaMenu';
import type { MenuLinkData } from '@/lib/api';

// Hardcoded fallback shown only until the mega-menu API responds (or if it
// fails) — the chrome must never render empty.
const fallbackLinks = [
  { label: 'View All', href: '/categories' },
  { label: 'Business Cards', href: '/categories/business-cards' },
  { label: 'Stationery & Letterheads', href: '/categories/letterheads' },
  { label: 'Banners & Posters', href: '/categories/banners-posters' },
  { label: 'Stamps & Ink', href: '/categories/stamps-seals' },
  { label: 'Labels & Stickers', href: '/categories/labels-stickers' },
  { label: 'Clothing & Apparel', href: '/categories/clothing-apparel' },
  { label: 'Mugs & Gifts', href: '/categories/mugs-gifts' },
  { label: 'Photo Products', href: '/categories/photo-products' },
  { label: 'Custom Packaging', href: '/categories/custom-packaging' },
];

type NavEntry =
  | { kind: 'viewall' }
  | { kind: 'mega'; item: MegaMenuItem; idx: number }
  | { kind: 'cms'; link: MenuLinkData };

type OpenKey = number | 'more' | null;

/**
 * Header navigation with an admin-managed mega menu (Content → Header Menu).
 * Tabs with dropdown content open a full-width panel on hover/click; tabs
 * without act as plain links. Entries that don't fit the bar collapse into a
 * trailing "More" tab (Priority+ pattern) — measured off an invisible clone
 * row so nothing ever overflows out of sight. Desktop-only — mobile
 * navigation lives in the bottom tab bar + drawer.
 */
export default function MainNav() {
  const pathname = useLocation().pathname;
  const items = useMegaMenu();
  // Admin-managed extras (Content → Site Menus → Header) appended after the
  // category tabs — e.g. Help Center. They collapse into "More" first.
  const headerLinks = useSiteMenus()?.header ?? [];

  const [open, setOpen] = useState<OpenKey>(null);
  const [visibleCount, setVisibleCount] = useState(Infinity);
  const hoverTimer = useRef<number | null>(null);
  const innerRef = useRef<HTMLDivElement | null>(null);
  const measureRef = useRef<HTMLDivElement | null>(null);

  const entries = useMemo<NavEntry[]>(() => {
    if (!items) return [];
    return [
      { kind: 'viewall' as const },
      ...items.map((item, idx) => ({ kind: 'mega' as const, item, idx })),
      ...headerLinks.map((link) => ({ kind: 'cms' as const, link })),
    ];
    // headerLinks comes from a hook without a stable identity; key on content.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, JSON.stringify(headerLinks)]);

  const hasPanel = (item: MegaMenuItem) =>
    (item.columns?.length ?? 0) > 0 || (item.featured?.length ?? 0) > 0;

  // --- Priority+ fit: measure the invisible clone row, keep what fits, and
  // collapse the tail under "More". Gap is 0 in .nav-inner so plain sums work.
  const compute = useCallback(() => {
    const inner = innerRef.current;
    const meas = measureRef.current;
    if (!inner || !meas || entries.length === 0) return;
    const kids = Array.from(meas.children) as HTMLElement[];
    if (kids.length !== entries.length + 1) return; // + trailing "More" clone
    const moreW = kids[kids.length - 1].getBoundingClientRect().width;
    const widths = kids.slice(0, -1).map((k) => k.getBoundingClientRect().width);
    const cs = getComputedStyle(inner);
    const avail = inner.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
    const total = widths.reduce((a, b) => a + b, 0);
    if (total <= avail) {
      setVisibleCount(widths.length);
      return;
    }
    let used = 0;
    let fit = 0;
    for (let n = 0; n < widths.length; n++) {
      if (used + widths[n] + moreW > avail) break;
      used += widths[n];
      fit = n + 1;
    }
    setVisibleCount(Math.max(1, fit)); // "View All" always stays
  }, [entries]);

  useLayoutEffect(() => {
    compute();
  }, [compute]);

  useEffect(() => {
    let raf = 0;
    const onResize = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(compute);
    };
    window.addEventListener('resize', onResize);
    // Web-font swap changes tab widths after first paint.
    document.fonts?.ready.then(compute).catch(() => {});
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
    };
  }, [compute]);

  // Route change closes any open panel.
  useEffect(() => {
    setOpen(null);
  }, [pathname]);

  // Escape closes the panel.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const scheduleOpen = (key: Exclude<OpenKey, null>) => {
    if (hoverTimer.current) window.clearTimeout(hoverTimer.current);
    // Small delay so skating the cursor across the bar doesn't flash panels.
    hoverTimer.current = window.setTimeout(() => setOpen(key), 90);
  };
  const cancelHover = () => {
    if (hoverTimer.current) window.clearTimeout(hoverTimer.current);
  };
  const close = () => setOpen(null);

  const isActive = (url: string) =>
    url !== '/' && (pathname === url || pathname.startsWith(url + '/') || pathname.startsWith(url + '?'));

  const tabButton = (key: Exclude<OpenKey, null>, label: string, badge?: string, active = false) => (
    <button
      key={typeof key === 'number' ? `mega-${key}` : 'more'}
      type="button"
      className={`meganav-tab ${open === key ? 'open' : ''} ${active ? 'active' : ''}`}
      aria-haspopup="true"
      aria-expanded={open === key}
      onMouseEnter={() => scheduleOpen(key)}
      onFocus={() => setOpen(key)}
      onClick={() => setOpen(open === key ? null : key)}
    >
      {label}
      {badge && <span className="meganav-badge">{badge}</span>}
    </button>
  );

  const renderEntry = (entry: NavEntry) => {
    if (entry.kind === 'viewall') {
      return (
        <Link key="viewall" to="/categories" className={pathname === '/categories' ? 'active' : ''}>
          View All
        </Link>
      );
    }
    if (entry.kind === 'mega') {
      const { item, idx } = entry;
      if (!hasPanel(item)) {
        return (
          <Link key={`mega-${idx}`} to={item.url || '#'} className={isActive(item.url) ? 'active' : ''}>
            {item.title}
            {item.badge && <span className="meganav-badge">{item.badge}</span>}
          </Link>
        );
      }
      // Vistaprint semantics: hover opens the panel, click goes to the page.
      // Tabs without a URL fall back to click-toggles (like "More").
      if (item.url) {
        return (
          <Link
            key={`mega-${idx}`}
            to={item.url}
            className={`meganav-tab ${open === idx ? 'open' : ''} ${isActive(item.url) ? 'active' : ''}`}
            aria-haspopup="true"
            aria-expanded={open === idx}
            onMouseEnter={() => scheduleOpen(idx)}
            onFocus={() => setOpen(idx)}
            onClick={close}
          >
            {item.title}
            {item.badge && <span className="meganav-badge">{item.badge}</span>}
          </Link>
        );
      }
      return tabButton(idx, item.title, item.badge, isActive(item.url));
    }
    const { link } = entry;
    return /^https?:\/\//i.test(link.url) || link.new_tab ? (
      <a key={`cms-${link.label}-${link.url}`} href={link.url} target={link.new_tab ? '_blank' : undefined} rel="noopener">
        {link.label}
      </a>
    ) : (
      <Link key={`cms-${link.label}-${link.url}`} to={link.url} className={pathname === link.url ? 'active' : ''}>
        {link.label}
      </Link>
    );
  };

  // Invisible clone used purely for width measurement — same classes, same
  // DOM shapes, laid out at natural width off to the side.
  const measureEntry = (entry: NavEntry, n: number) => {
    if (entry.kind === 'viewall') return <a key={n}>View All</a>;
    if (entry.kind === 'mega') {
      const { item } = entry;
      const badge = item.badge && <span className="meganav-badge">{item.badge}</span>;
      return hasPanel(item) ? (
        <span key={n} className="meganav-tab">
          {item.title}
          {badge}
        </span>
      ) : (
        <a key={n}>
          {item.title}
          {badge}
        </a>
      );
    }
    return <a key={n}>{entry.link.label}</a>;
  };

  const visible = entries.slice(0, visibleCount);
  const collapsed = entries.slice(visibleCount);
  const collapsedMega = collapsed.filter((e): e is Extract<NavEntry, { kind: 'mega' }> => e.kind === 'mega');
  const collapsedCms = collapsed.filter((e): e is Extract<NavEntry, { kind: 'cms' }> => e.kind === 'cms');
  const openMega = items && typeof open === 'number' ? items[open] : null;

  const featuredCards = (item: MegaMenuItem) =>
    item.featured.length > 0 && (
      <div className={`meganav-featured-wrap ${item.columns.length === 0 ? 'full' : ''}`}>
        {item.featured.map((card, n) => (
          <Link key={n} to={card.url || item.url || '#'} className="meganav-featured" onClick={close}>
            {card.image && <img src={card.image} alt={card.title || item.title} loading="lazy" />}
            {card.title && <span>{card.title}</span>}
          </Link>
        ))}
      </div>
    );

  return (
    <nav className="main-nav" onMouseLeave={() => { cancelHover(); close(); }}>
      <div className="nav-inner" ref={innerRef}>
        {items ? (
          <>
            {visible.map(renderEntry)}
            {collapsed.length > 0 && tabButton('more', 'More')}
            <div className="nav-measure" ref={measureRef} aria-hidden="true">
              {entries.map(measureEntry)}
              <span className="meganav-tab">More</span>
            </div>
          </>
        ) : (
          <>
            {fallbackLinks.map((link) => (
              <Link key={link.href} to={link.href} className={pathname === link.href ? 'active' : ''}>
                {link.label}
              </Link>
            ))}
            {headerLinks.map((link) => renderEntry({ kind: 'cms', link }))}
          </>
        )}
      </div>

      {/* Full-width dropdown for the open tab. Lives inside the nav so one
          onMouseLeave on the wrapper closes everything. */}
      {openMega && hasPanel(openMega) && (
        <div className="meganav-panel" role="region" aria-label={`${openMega.title} menu`}>
          <div className="meganav-panel-inner">
            {openMega.columns.length > 0 && (
              <div className="meganav-cols">
                {openMega.columns.map((col, ci) => (
                  <div className="meganav-col" key={`${col.heading}-${ci}`}>
                    {col.heading && <p className="meganav-heading">{col.heading}</p>}
                    {col.links.map((link, li) => (
                      <Link key={`${link.label}-${li}`} to={link.url || '#'} className="meganav-link" onClick={close}>
                        {link.label}
                        {link.badge && <span className="meganav-badge">{link.badge}</span>}
                      </Link>
                    ))}
                  </div>
                ))}
              </div>
            )}
            {featuredCards(openMega)}
            {openMega.url && (
              <Link to={openMega.url} className="meganav-seeall" onClick={close}>
                See all {openMega.title} →
              </Link>
            )}
          </div>
        </div>
      )}

      {/* Overflow panel: every collapsed tab as a column of its top links. */}
      {open === 'more' && collapsed.length > 0 && (
        <div className="meganav-panel" role="region" aria-label="More categories">
          <div className="meganav-panel-inner">
            <div className="meganav-cols">
              {collapsedMega.map(({ item, idx }) => (
                <div className="meganav-col" key={`more-${idx}`}>
                  <Link to={item.url || '#'} className="meganav-heading" onClick={close}>
                    {item.title}
                    {item.badge && <span className="meganav-badge">{item.badge}</span>}
                  </Link>
                  {item.columns[0]?.links.slice(0, 5).map((link, li) => (
                    <Link key={li} to={link.url || '#'} className="meganav-link" onClick={close}>
                      {link.label}
                    </Link>
                  ))}
                </div>
              ))}
              {collapsedCms.length > 0 && (
                <div className="meganav-col">
                  <p className="meganav-heading">More</p>
                  {collapsedCms.map(({ link }) =>
                    /^https?:\/\//i.test(link.url) || link.new_tab ? (
                      <a key={link.url} href={link.url} className="meganav-link" target={link.new_tab ? '_blank' : undefined} rel="noopener">
                        {link.label}
                      </a>
                    ) : (
                      <Link key={link.url} to={link.url} className="meganav-link" onClick={close}>
                        {link.label}
                      </Link>
                    ),
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
